import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CalendarDays, Download, FileSpreadsheet, FileText, Footprints, IndianRupee, Loader2, MapPinned, MoreHorizontal, RotateCcw,
  ShieldCheck, Sunrise, UserRoundCheck, X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MultiSelect } from "../ui/multi-select";
import { splitIds, joinIds } from "../TerritoryFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { api, downloadBlob, errMsg, fmtDay, istDateISO } from "../../lib/api";
import { FetchButton, FetchDirtyHint, FetchPrompt, useFetchGate } from "../FetchBar";
import { PaginationBar, TableRowsSkeleton, usePageSlice } from "../PaginationBar";
import { StaffLabel } from "../StaffAvatar";
import { ReasonDialog } from "./ReasonDialog";
import { STATUS_META, STATUS_ORDER, StatusBadge, WEEKDAYS, inr, n, statusLabel, useAttendanceOptions, weekdayOf } from "./shared";

const OVERRIDE_CHOICES = [
  { key: "present", label: "Present (full day)", icon: UserRoundCheck },
  { key: "half_day", label: "Half Day", icon: Sunrise },
  { key: "absent", label: "Absent", icon: X },
];
const monthStart = (iso) => iso.slice(0, 8) + "01";

/**
 * Phase 168 — admin attendance register. Fetch loads the date range from the server (metrics are recomputed on every
 * fetch); every other filter is applied instantly on the loaded rows. Exports send the same filters to the server.
 */
export function AttendanceRecordsTab({ code, onChanged }) {
  const today = istDateISO();
  const { opts, error: optsError } = useAttendanceOptions(code);
  const [range, setRange] = useState({ date_from: monthStart(today), date_to: today });
  const gate = useFetchGate(range);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // instant filters (client-side on the loaded range)
  const [zoneId, setZoneId] = useState("");
  const [wardId, setWardId] = useState("");
  const [collectorId, setCollectorId] = useState("");
  const [supervisorId, setSupervisorId] = useState("");
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState(() => new Set());
  const [overridden, setOverridden] = useState("all");
  const [exporting, setExporting] = useState(null);
  const [action, setAction] = useState(null); // {kind, rec, status}

  const load = async (applied) => {
    if (!applied) return;
    setLoading(true);
    try {
      const r = await api.get(`/t/${code}/attendance/records`, { params: applied });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (gate.fetched) load(gate.applied); }, [gate.applied, gate.version]); // eslint-disable-line

  // Phase 176: Zone / Ward are multi-select (comma-joined ids); any selected id matches
  const zoneSel = useMemo(() => splitIds(zoneId), [zoneId]);
  const wardSel = useMemo(() => splitIds(wardId), [wardId]);
  const wards = useMemo(() => (opts?.wards || []).filter((w) => !zoneSel.length || zoneSel.includes(w.zone_id)), [opts, zoneSel]);
  const collectors = useMemo(() => (opts?.collectors || []).filter((c) =>
    (!zoneSel.length || (c.zone_ids || []).some((z) => zoneSel.includes(z))) && (!wardSel.length || (c.ward_ids || []).some((w) => wardSel.includes(w))) && (!supervisorId || c.reports_to_id === supervisorId)), [opts, zoneSel, wardSel, supervisorId]);
  useEffect(() => {
    const keep = wardSel.filter((w) => wards.some((x) => x.id === w));
    if (keep.length !== wardSel.length) setWardId(joinIds(keep));
  }, [wards, wardSel]);
  useEffect(() => { if (collectorId && !collectors.some((c) => c.id === collectorId)) setCollectorId(""); }, [collectors, collectorId]);

  const filtered = useMemo(() => {
    let rows = data?.records || [];
    if (zoneSel.length) rows = rows.filter((r) => (r.zone_ids || []).some((z) => zoneSel.includes(z)));
    if (wardSel.length) rows = rows.filter((r) => (r.ward_ids || []).some((w) => wardSel.includes(w)));
    if (collectorId) rows = rows.filter((r) => r.collector_id === collectorId);
    if (supervisorId) rows = rows.filter((r) => r.reports_to_id === supervisorId);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      rows = rows.filter((r) => (r.collector_name || "").toLowerCase().includes(s) || (r.employee_id || "").toLowerCase().includes(s));
    }
    if (statuses.size) rows = rows.filter((r) => statuses.has(r.status));
    if (overridden === "yes") rows = rows.filter((r) => r.override);
    if (overridden === "no") rows = rows.filter((r) => !r.override);
    return rows;
  }, [data, zoneSel, wardSel, collectorId, supervisorId, search, statuses, overridden]);

  const counts = useMemo(() => {
    const c = { total: filtered.length, overridden: 0, visits: 0, collection: 0 };
    STATUS_ORDER.forEach((s) => { c[s] = 0; });
    filtered.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; if (r.override) c.overridden += 1; c.visits += r.visits || 0; c.collection += r.collection || 0; });
    return c;
  }, [filtered]);
  const pager = usePageSlice(filtered, 25);
  useEffect(() => { pager.resetPage(); }, [zoneId, wardId, collectorId, supervisorId, search, statuses, overridden, data]); // eslint-disable-line

  const exportParams = () => ({
    ...gate.applied, zone_id: zoneId || undefined, ward_id: wardId || undefined, collector_id: collectorId || undefined, supervisor_id: supervisorId || undefined,
    search: search.trim() || undefined, status: statuses.size ? [...statuses].join(",") : undefined, overridden: overridden === "all" ? undefined : overridden,
  });
  const doExport = async (format) => {
    setExporting(format);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries({ format, ...exportParams() }).filter(([, v]) => v != null && v !== "")));
      await downloadBlob(`/t/${code}/attendance/records/export?${qs.toString()}`, `attendance.${format}`);
      toast.success(`Attendance exported (${format === "pdf" ? "PDF" : "Excel"}, ${filtered.length} ${filtered.length === 1 ? "row" : "rows"})`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(null);
    }
  };

  const toggleStatus = (s) => setStatuses((prev) => { const next = new Set(prev); next.has(s) ? next.delete(s) : next.add(s); return next; });
  const clearFilters = () => { setZoneId(""); setWardId(""); setCollectorId(""); setSupervisorId(""); setSearch(""); setStatuses(new Set()); setOverridden("all"); };
  const anyFilter = zoneId || wardId || collectorId || supervisorId || search || statuses.size || overridden !== "all";

  const afterAction = (msg) => { toast.success(msg); load(gate.applied); onChanged?.(); };
  const runOverride = async (reason) => {
    const { rec, status } = action;
    try {
      await api.post(`/t/${code}/attendance/override`, { collector_id: rec.collector_id, day: rec.day, status, reason });
      setAction(null);
      afterAction(`${rec.collector_name} marked ${statusLabel(status)} for ${fmtDay(rec.day)}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const runClear = async (reason) => {
    const { rec } = action;
    try {
      const r = await api.post(`/t/${code}/attendance/override/clear`, { collector_id: rec.collector_id, day: rec.day, reason });
      setAction(null);
      afterAction(`Override removed — ${rec.collector_name} is ${statusLabel(r.data.status)} for ${fmtDay(rec.day)}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const runFieldDuty = async (reason) => {
    const { rec, on } = action;
    try {
      await api.post(`/t/${code}/attendance/field-duty`, { collector_id: rec.collector_id, day: rec.day, on, reason });
      setAction(null);
      afterAction(on ? `${rec.collector_name} marked on field duty for ${fmtDay(rec.day)}` : `Field duty removed for ${rec.collector_name} on ${fmtDay(rec.day)}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const runSunday = async (reason) => {
    const { rec } = action;
    try {
      await api.post(`/t/${code}/attendance/working-sunday`, { day: rec.day, on: true, reason });
      setAction(null);
      afterAction(`Sunday ${fmtDay(rec.day)} is now a working day — attendance is tracked normally`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k) => (e) => setRange((r) => ({ ...r, [k]: e.target.value }));

  return (
    <div className="space-y-3" data-testid="attendance-records-tab">
      <Card className="shadow-sm">
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" className="h-10 w-[150px]" value={range.date_from} max={range.date_to || undefined} onChange={set("date_from")} data-testid="attendance-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" className="h-10 w-[150px]" value={range.date_to} min={range.date_from || undefined} max={today} onChange={set("date_to")} data-testid="attendance-date-to" />
            </div>
            <div className="flex gap-1">
              {[["Today", today, today], ["This month", monthStart(today), today], ["Last 7 days", istDateISO(new Date(Date.now() - 6 * 86400000)), today]].map(([l, f, t]) => (
                <Button key={l} type="button" variant="ghost" size="sm" className="h-10 text-xs" onClick={() => setRange({ date_from: f, date_to: t })} data-testid={`attendance-quick-${l.toLowerCase().replace(/\s+/g, "-")}`}>{l}</Button>
              ))}
            </div>
            <FetchButton onClick={gate.fetch} loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="attendance-fetch-button"
              disabled={!range.date_from || !range.date_to || range.date_from > range.date_to} />
            <FetchDirtyHint dirty={gate.dirty} />
            {gate.fetched && data && (
              <span className="text-xs text-muted-foreground ml-auto" data-testid="attendance-fetched-at">
                Loaded {n(data.records.length)} records · {n(data.summary_all?.collectors)} collectors · metrics recomputed on every fetch
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3 border-t pt-3">
            <div className="space-y-1">
              <Label className="text-xs">Zone</Label>
              <div className="w-[170px]"><MultiSelect options={(opts?.zones || []).map((z) => ({ value: z.id, label: z.name }))} value={zoneSel} onChange={(ids) => setZoneId(joinIds(ids))}
                allLabel="All zones" noun="zone" placeholder="Search zones…" className="h-9" testId="attendance-filter-zone" /></div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ward</Label>
              <div className="w-[180px]"><MultiSelect options={wards.map((w) => ({ value: w.id, label: w.name, group: zoneSel.length !== 1 ? (opts?.zones || []).find((z) => z.id === w.zone_id)?.name : undefined }))} value={wardSel} onChange={(ids) => setWardId(joinIds(ids))}
                allLabel="All wards" noun="ward" placeholder="Search wards…" className="h-9" testId="attendance-filter-ward" /></div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Supervisor</Label>
              <Select value={supervisorId || "all"} onValueChange={(v) => setSupervisorId(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-[170px]" data-testid="attendance-filter-supervisor"><SelectValue placeholder="All supervisors" /></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="all">All supervisors</SelectItem>{(opts?.supervisors || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Collector</Label>
              <Select value={collectorId || "all"} onValueChange={(v) => setCollectorId(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-[190px]" data-testid="attendance-filter-collector"><SelectValue placeholder="All collectors" /></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="all">All collectors</SelectItem>{collectors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.employee_id ? ` (${c.employee_id})` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Collector name / code</Label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type to filter…" className="h-9 w-[170px]" data-testid="attendance-filter-search" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Override</Label>
              <Select value={overridden} onValueChange={setOverridden}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="attendance-filter-overridden"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All records</SelectItem>
                  <SelectItem value="yes">Overridden only</SelectItem>
                  <SelectItem value="no">Not overridden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="ghost" size="sm" className="h-9 text-xs" onClick={clearFilters} disabled={!anyFilter} data-testid="attendance-clear-filters"><X className="h-3.5 w-3.5 mr-1" /> Clear filters</Button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="attendance-status-chips">
            <span className="text-xs text-muted-foreground mr-1">Status:</span>
            {STATUS_ORDER.map((s) => {
              const on = statuses.has(s);
              const m = STATUS_META[s];
              return (
                <button key={s} type="button" onClick={() => toggleStatus(s)} aria-pressed={on} data-testid={`attendance-status-chip-${s}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${on ? `${m.cls} ring-1 ring-current` : "bg-background text-muted-foreground hover:bg-muted"}`}>
                  <span className={`h-2 w-2 rounded-full ${m.dot}`} aria-hidden="true" /> {m.label}
                  {gate.fetched && <span className="tabular-nums opacity-80">{n(counts[s] || 0)}</span>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {optsError && <p className="text-xs text-destructive">Could not load filter options: {optsError}</p>}

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent><FetchPrompt onFetch={gate.fetch} testId="attendance-fetch-prompt"
          subtitle="Pick the date range above and click Fetch. Zone, ward, collector, status and override filters then apply instantly to the loaded records." /></CardContent></Card>
      ) : error ? (
        <Card className="shadow-sm border-destructive/40"><CardContent className="p-4 text-sm text-destructive flex items-center justify-between gap-3" data-testid="attendance-error">
          <span>Could not load attendance: {error}</span><Button variant="outline" size="sm" onClick={() => load(gate.applied)} data-testid="attendance-retry">Retry</Button></CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="attendance-summary">
            {[["Records", counts.total, "records"], ["Present", (counts.present || 0) + (counts.field_visit || 0), "present"], ["Half day", counts.half_day || 0, "half-day"],
              ["Absent", counts.absent || 0, "absent"], ["Leave", counts.leave || 0, "leave"], ["Overridden", counts.overridden, "overridden"],
              ["Visits", n(counts.visits), "visits"], ["Collection", inr(counts.collection), "collection"]].map(([l, v, k]) => (
              <Card key={k} className="shadow-sm"><CardContent className="p-2.5">
                <p className="text-[11px] text-muted-foreground">{l}</p>
                <p className="text-base font-semibold tabular-nums font-heading" data-testid={`attendance-summary-${k}`}>{v}</p>
              </CardContent></Card>
            ))}
          </div>

          <Card className="shadow-sm">
            <CardContent className="p-0">
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b">
                <p className="text-sm font-medium flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" /> Attendance register
                  <span className="text-xs font-normal text-muted-foreground">{fmtDay(gate.applied.date_from)} – {fmtDay(gate.applied.date_to)}{anyFilter ? " · filtered" : ""}</span></p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => doExport("xlsx")} disabled={!!exporting || filtered.length === 0} data-testid="attendance-export-xlsx">
                    {exporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />} Excel
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => doExport("pdf")} disabled={!!exporting || filtered.length === 0} data-testid="attendance-export-pdf">
                    {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />} PDF
                  </Button>
                  <span className="text-[11px] text-muted-foreground hidden md:inline"><Download className="h-3 w-3 inline mr-1" />exports the {n(filtered.length)} filtered {filtered.length === 1 ? "row" : "rows"}</span>
                </div>
              </div>
              {loading && !data ? <TableRowsSkeleton rows={6} cols={8} testId="attendance-skeleton" /> : filtered.length === 0 ? (
                <p className="px-4 py-10 text-sm text-muted-foreground text-center" data-testid="attendance-empty">
                  {data?.records?.length ? "No records match the current filters." : "No attendance records in this range — collectors must exist on those dates."}
                </p>
              ) : (
                <TooltipProvider delayDuration={200}>
                  <Table data-testid="attendance-table">
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Collector</TableHead>
                      <TableHead className="text-xs hidden lg:table-cell">Zone / Ward</TableHead><TableHead className="text-xs">Sign-in</TableHead>
                      <TableHead className="text-xs text-right">Visits</TableHead><TableHead className="text-xs text-right">Collection</TableHead>
                      <TableHead className="text-xs">Status</TableHead><TableHead className="text-xs hidden md:table-cell">Reason</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {pager.slice.map((r) => {
                        const rid = `${r.collector_id}-${r.day}`;
                        const working = r.day_kind === "working";
                        const sunday = weekdayOf(r.day) === 6;
                        return (
                          <TableRow key={rid} data-testid={`attendance-row-${rid}`} data-status={r.status} data-overridden={!!r.override} className={r.override ? "bg-amber-50/40" : ""}>
                            <TableCell className="py-2 whitespace-nowrap">
                              <span className="text-sm">{fmtDay(r.day)}</span>
                              <span className="block text-[10px] text-muted-foreground">{WEEKDAYS[weekdayOf(r.day)]}{r.provisional ? " · live" : r.finalised ? " · final" : ""}</span>
                            </TableCell>
                            <TableCell className="py-2">
                              <StaffLabel code={code} userId={r.collector_id} name={r.collector_name} empCode={r.employee_id} />
                              {r.reports_to_name && <span className="block text-[10px] text-muted-foreground">Reports to {r.reports_to_name}</span>}
                            </TableCell>
                            <TableCell className="py-2 hidden lg:table-cell text-xs text-muted-foreground">
                              {(r.zone_names || []).join(", ") || "—"}<span className="block truncate max-w-[180px]">{(r.ward_names || []).join(", ")}</span>
                            </TableCell>
                            <TableCell className="py-2 whitespace-nowrap text-xs">
                              {r.login_time_ist ? <span className="tabular-nums">{r.login_time_ist}</span> : <span className="text-muted-foreground">—</span>}
                              {r.late && <Badge variant="outline" className="ml-1 text-[10px] border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))]" data-testid={`attendance-late-${rid}`}>Late{r.late_ordinal ? ` #${r.late_ordinal}` : ""}</Badge>}
                              {r.field_duty && <Badge variant="outline" className="ml-1 text-[10px] border-[hsl(var(--info))]/30 text-[hsl(var(--info))]" data-testid={`attendance-field-duty-${rid}`}><MapPinned className="h-2.5 w-2.5 mr-0.5" />Field duty</Badge>}
                            </TableCell>
                            <TableCell className="py-2 text-right tabular-nums text-sm"><Footprints className="h-3 w-3 inline mr-1 text-muted-foreground" />{n(r.visits)}</TableCell>
                            <TableCell className="py-2 text-right tabular-nums text-sm"><IndianRupee className="h-3 w-3 inline text-muted-foreground" />{n(r.collection)}</TableCell>
                            <TableCell className="py-2">
                              <StatusBadge status={r.status} testId={`attendance-status-${rid}`} />
                              {r.override && (
                                <Tooltip><TooltipTrigger asChild>
                                  <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 bg-amber-50 text-amber-900 cursor-help" data-testid={`attendance-overridden-${rid}`}>
                                    <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Overridden
                                  </Badge></TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-xs">Was {statusLabel(r.override.status_from)} · by {r.override.admin_name} on {fmtDay(r.override.at)} — {r.override.reason}</TooltipContent></Tooltip>
                              )}
                              {r.pending_leave_request_id && !r.leave_request_id && <Badge variant="outline" className="ml-1 text-[10px]" data-testid={`attendance-pending-leave-${rid}`}>Leave pending</Badge>}
                            </TableCell>
                            <TableCell className="py-2 hidden md:table-cell text-xs text-muted-foreground max-w-[320px]">
                              <span className="line-clamp-2" title={r.status_reason} data-testid={`attendance-reason-${rid}`}>{r.status_reason}</span>
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${r.collector_name} on ${r.day}`} data-testid={`attendance-actions-${rid}`}><MoreHorizontal className="h-4 w-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-60">
                                  {working ? (
                                    <>
                                      <DropdownMenuLabel className="text-xs">Override attendance</DropdownMenuLabel>
                                      {OVERRIDE_CHOICES.filter((c) => c.key !== r.status).map((c) => (
                                        <DropdownMenuItem key={c.key} onClick={() => setAction({ kind: "override", rec: r, status: c.key })} data-testid={`attendance-override-${c.key}-${rid}`}>
                                          <c.icon className="h-4 w-4 mr-2" /> Mark {c.label}
                                        </DropdownMenuItem>
                                      ))}
                                      {r.override && <DropdownMenuItem onClick={() => setAction({ kind: "clear", rec: r })} data-testid={`attendance-clear-override-${rid}`}><RotateCcw className="h-4 w-4 mr-2" /> Remove override (back to {statusLabel(r.computed_status)})</DropdownMenuItem>}
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => setAction({ kind: "field", rec: r, on: !r.field_duty })} data-testid={`attendance-field-duty-toggle-${rid}`}>
                                        <MapPinned className="h-4 w-4 mr-2" /> {r.field_duty ? "Remove field duty" : "Mark on field duty"}
                                      </DropdownMenuItem>
                                    </>
                                  ) : sunday && r.status === "weekly_off" ? (
                                    <DropdownMenuItem onClick={() => setAction({ kind: "sunday", rec: r })} data-testid={`attendance-sunday-working-${rid}`}><AlertTriangle className="h-4 w-4 mr-2" /> Make this Sunday a working day</DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{r.status === "holiday" ? "Holiday — manage it on the Holidays tab" : "No actions for this day"}</DropdownMenuLabel>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TooltipProvider>
              )}
              {filtered.length > 0 && <div className="px-3 pb-3"><PaginationBar page={pager.page} limit={pager.limit} total={pager.total} onPageChange={pager.setPage} onLimitChange={pager.setLimit} testId="attendance-pagination" /></div>}
            </CardContent>
          </Card>
        </>
      )}

      <ReasonDialog open={action?.kind === "override"} onOpenChange={(v) => !v && setAction(null)} icon={ShieldCheck}
        title={action?.kind === "override" ? `Mark ${action.rec.collector_name} ${statusLabel(action.status)}` : ""}
        description={action?.kind === "override" ? `${fmtDay(action.rec.day)} — currently ${statusLabel(action.rec.status)} (${action.rec.visits} visits, ${inr(action.rec.collection)}). The collector is notified in-app${action.status !== "present" ? " with your reason" : ""}.` : ""}
        confirmLabel={action?.kind === "override" ? `Mark ${statusLabel(action.status)}` : "Confirm"} onConfirm={runOverride} testId="override-dialog" />
      <ReasonDialog open={action?.kind === "clear"} onOpenChange={(v) => !v && setAction(null)} icon={RotateCcw} title="Remove override"
        description={action?.kind === "clear" ? `${action.rec.collector_name} on ${fmtDay(action.rec.day)} goes back to the system result (${statusLabel(action.rec.computed_status)}).` : ""}
        confirmLabel="Remove override" onConfirm={runClear} testId="clear-override-dialog" />
      <ReasonDialog open={action?.kind === "field"} onOpenChange={(v) => !v && setAction(null)} icon={MapPinned}
        title={action?.on ? "Mark on field duty" : "Remove field duty"} optional={!!action?.on}
        description={action?.kind === "field" ? (action.on
          ? `${action.rec.collector_name} on ${fmtDay(action.rec.day)}: the late sign-in rule is ignored for this day, but the ${action.rec.visits != null ? "visit / collection requirement" : "requirement"} still applies.`
          : `${action.rec.collector_name} on ${fmtDay(action.rec.day)} will follow the normal late sign-in rule again.`) : ""}
        confirmLabel={action?.on ? "Mark field duty" : "Remove field duty"} onConfirm={runFieldDuty} testId="field-duty-dialog" />
      <ReasonDialog open={action?.kind === "sunday"} onOpenChange={(v) => !v && setAction(null)} icon={AlertTriangle} title="Make this Sunday a working day"
        description={action?.kind === "sunday" ? `Sunday ${fmtDay(action.rec.day)} becomes a working day for every collector — attendance is tracked normally and all staff are notified.` : ""}
        confirmLabel="Make working day" onConfirm={runSunday} testId="sunday-working-dialog" />
    </div>
  );
}
